

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import Typography from '../../components/Typography/Typography';
import TextField from '../../components/TextField/TextField';
import Button from '../../components/Button/Button';
import CONSTANTS from '../../contants';
import { RootState } from '../../store';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state: RootState) => state.auth);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) setEmailError('');
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(e.target.value);
    if (passwordError) setPasswordError('');
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = () => {
    let isValid = true;
    
    if (!email) {
      setEmailError('Email is required');
      isValid = false;
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      setEmailError('Please enter a valid email');
      isValid = false;
    }
    
    if (!password) {
      setPasswordError('Password is required');
      isValid = false;
    } else if (password.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      isValid = false;
    }
    
    return isValid;
  };

  const handleLogin = async () => {
    if (validateForm()) {
      try {
        const result = await dispatch.auth.loginAsync({ email, password });
        
        if (result.success) {
          toast.success(result.message || 'Login successful!');
          navigate('/dashboard');
        } else {
          toast.error(result.message || 'Login failed. Please try again.');
        }
      } catch (error) {
        toast.error('An unexpected error occurred. Please try again.');
      }
    }
  };

  return (
    <div className='flex-1 flex items-center justify-center px-4 py-8'>
      <div className='w-full max-w-md mx-auto'>
        {/* Login Modal */}
        <div className='!bg-[#FFFFFF] rounded-3xl border-2 border-[#E2E8F0] shadow-auth_box p-6 sm:p-8 w-full relative overflow-hidden'>
          {/* Decorative border gradient */}
          <div className='absolute inset-0 rounded-3xl  pointer-events-none'></div>
          
          {/* Header */}
          <div className='text-center mb-6 sm:mb-8 relative z-10'>
            <Typography
              variant='h1'
              className='mb-2 text-blue-900 font-bold title text-2xl sm:text-2xl md:text-2xl lg:text-3xl'
            >
              KPMG Dashboard
            </Typography>
          
            <Typography variant='body1' color='grey' className='text-k-grey-text text-[16px] '>
            Please enter your email and password to log in
            </Typography>
          </div>

          {/* Form */}
          <div className='space-y-4 sm:space-y-4 relative z-10'>
            {/* Email Field */}
            <TextField
              label='Email Address'
              type='email'
              value={email}
              onChange={handleEmailChange}
              error={emailError}
              required
              helperText=''
              onClear={() => {
                setEmail('');
                setEmailError('');
              }}
              className='w-full'
            />

            {/* Password Field */}
            <TextField
              label='Password'
              type='password'
              value={password}
              onChange={handlePasswordChange}
              error={passwordError}
              required
              helperText=''
              onClear={() => {
                setPassword('');
                setPasswordError('');
              }}
              showPasswordToggle={true}
              showPassword={showPassword}
              onTogglePassword={togglePasswordVisibility}
              className='w-full '
            />

            {/* Login Button */}
            <div className='pt-2 sm:pt-3'>
              <Button
                label={loading ? 'Logging In...' : 'Log In'}
                variant='primary'
                className='w-full h-11 sm:h-12 text-base sm:text-lg font-semibold bg-g_gradient hover:opacity-90 transition-opacity disabled:opacity-50'
                onClick={handleLogin}
                disabled={loading}
              />
            </div>
          </div>

          {/* Footer */}
          <div className='mt-5 sm:mt-6 text-center relative z-10'>
            <Typography variant='body2' color='grey' className='text-black text-[14px]'>
              Forgot Password?{' '}
              <span 
                className='text-[#1621C9] cursor-pointer underline font-medium'
                onClick={() => navigate('/reset-password')}
              >
                Reset Password
              </span>
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
